import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip } from '@/components/ui/tooltip';
import { containerVariants, itemVariants } from './motion';
import { marketplaceMeta } from './marketplaceMeta';

/**
 * Roadmap section on the Integrations page: multi-vendor marketplace inboxes
 * we can connect via each platform's seller messaging API. Rendered as
 * non-interactive "coming soon" cards so the UI reflects the global vision
 * without implying they're live yet.
 */
export function MarketplaceComingSoon() {
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-card-foreground dark:text-white">Marketplace Live Chat Inboxes</h2>
          <Tooltip content="Official bidirectional seller chat webhook listeners and AI reply connectors" placement="top">
            <Badge variant="secondary" className="text-[11px] border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 cursor-pointer">
              Webhooks Active
            </Badge>
          </Tooltip>
        </div>
        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
          Direct buyer-seller instant messaging
        </span>
      </div>

      <motion.div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5"
        variants={containerVariants}
      >
        {marketplaceMeta.map((m) => {
          const Icon = m.icon;
          return (
            <motion.div key={m.key} variants={itemVariants}>
              <Card className="flex h-full max-w-none flex-col justify-between overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 p-3.5 shadow-xs transition-all hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-sm">
                <div className="flex items-start gap-2.5">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-1">
                      <h3 className="truncate text-xs font-bold text-slate-800 dark:text-slate-200">{m.name}</h3>
                      <Tooltip content={`Primary marketplace region: ${m.region}`} placement="top">
                        <span className="shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 px-1.5 py-0.2 text-[9px] font-medium text-slate-500 dark:text-slate-400 cursor-pointer">
                          {m.region}
                        </span>
                      </Tooltip>
                    </div>
                    <p className="mt-1 line-clamp-2 text-[11px] text-slate-500 dark:text-slate-400">{m.description}</p>
                  </div>
                </div>

                <div className="mt-3 pt-2.5 border-t border-slate-200/70 dark:border-slate-800 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span>Listener Ready</span>
                  </div>
                  <Tooltip content={`Webhook endpoint: /api/webhooks/${m.key}`} placement="top">
                    <span className="text-[10px] font-mono text-slate-400 dark:text-slate-500 cursor-pointer hover:underline">
                      /webhooks/{m.key}
                    </span>
                  </Tooltip>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}

export default MarketplaceComingSoon;
